import React, { useState, useEffect } from 'react';
import InfoIcon from '../../assets/info.svg';
import './TeamInventorySection.css';

function TeamInventorySection({ onMemberSelect, selectedMemberId }) {
  const [teamData, setTeamData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [viewMode, setViewMode] = useState('direct'); // 'direct' or 'downline'
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    fetchTeamData();
  }, [searchTerm, offset, viewMode]);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: 20,
        offset: offset,
        view_mode: viewMode
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`/backend/api/inventory/team-inventory.php?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          if (offset === 0) {
            setTeamData(data);
          } else {
            setTeamData(prev => ({
              ...data,
              team_inventory: [...prev.team_inventory, ...data.team_inventory]
            }));
          }
          setHasMore(data.pagination.has_more);
        }
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setOffset(0);
  };

  const handleLoadMore = () => {
    setOffset(prev => prev + 10);
  };

  const handleMemberClick = (member) => {
    onMemberSelect(member);
  };

  const formatGrowth = (growth) => {
    if (!growth) return '';
    const sign = growth > 0 ? '+' : '';
    return `${sign}${growth}%`;
  };

  const getGrowthColor = (growth) => {
    if (!growth) return '#666';
    return growth > 0 ? '#4CAF50' : growth < 0 ? '#F44336' : '#666';
  };

  const handleInfoHover = (e, text) => {
    setShowTooltip(true);
    setTooltipData(text);
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleInfoMove = (e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleInfoLeave = () => {
    setShowTooltip(false);
    setTooltipData(null);
  };

  if (loading && !teamData) {
    return <div className="team-loading">Loading team data...</div>;
  }

  if (!teamData) {
    return <div className="team-error">Failed to load team data</div>;
  }

  return (
    <div className="team-inventory-section">
      {/* Team Summary */}
      <div className="team-summary">
        <h3>Team Overview</h3>
        <div className="summary-stats">
          <div className="summary-item">
            <span className="summary-label">Total Team Members</span>
            <span className="summary-value">{teamData.team_summary.total_team_members}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">With Inventory</span>
            <span className="summary-value success">{teamData.team_summary.members_with_inventory}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Without Inventory</span>
            <span className="summary-value warning">{teamData.team_summary.members_without_inventory}</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="team-search">
        <input
          type="text"
          placeholder="Search team members by name or email..."
          value={searchTerm}
          onChange={handleSearch}
          className="search-input"
        />
      </div>

      {/* Team Members List */}
      <div className="team-members">
        <div className="team-members-header">
          <h3>Team Members</h3>
          <div className="view-mode-toggle">
            <button 
              className={`toggle-btn ${viewMode === 'direct' ? 'active' : ''}`}
              onClick={() => setViewMode('direct')}
            >
              Direct
            </button>
            <button 
              className={`toggle-btn ${viewMode === 'downline' ? 'active' : ''}`}
              onClick={() => setViewMode('downline')}
            >
              Downline
            </button>
            <div 
              className="info-icon-wrapper"
              onMouseEnter={(e) => handleInfoHover(e, viewMode === 'direct' ? "Direct Mentees: Shows only people you directly invited" : "Entire Downline: Shows all people in your network tree")}
              onMouseMove={handleInfoMove}
              onMouseLeave={handleInfoLeave}
            >
              <img src={InfoIcon} alt="Info" className="info-icon" />
            </div>
          </div>
        </div>
        {teamData.team_inventory.length === 0 ? (
          <div className="no-members">
            {searchTerm ? 'No team members found matching your search.' : 'No team members found.'}
          </div>
        ) : (
          <div className="members-table">
            {teamData.team_inventory.map((member) => (
              <div 
                key={member.user.id} 
                className={`member-row ${selectedMemberId === member.user.id ? 'selected' : ''}`}
                onClick={() => handleMemberClick(member.user)}
              >
                <div className="member-cell">
                  <div className="member-name">
                    {member.user.first_name} {member.user.last_name}
                  </div>
                  <div className="member-email">{member.user.email}</div>
                </div>
                <div className="member-cell">
                  <div className="member-metric">
                    <span className="metric-label">Infinity:</span>
                    {member.has_inventory ? (
                      <span className="metric-value">
                        {member.latest_snapshot.infinity_total.toLocaleString()}
                        {member.growth?.infinity_total && (
                          <span 
                            className="growth-indicator"
                            style={{ color: getGrowthColor(member.growth.infinity_total) }}
                          >
                            {formatGrowth(member.growth.infinity_total)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="no-data">No data</span>
                    )}
                  </div>
                </div>
                <div className="member-cell">
                  <div className="member-metric">
                    <span className="metric-label">Monthly:</span>
                    {member.has_inventory ? (
                      <span className="metric-value">
                        {member.latest_snapshot.monthly_total.toLocaleString()}
                        {member.growth?.monthly_total && (
                          <span 
                            className="growth-indicator"
                            style={{ color: getGrowthColor(member.growth.monthly_total) }}
                          >
                            {formatGrowth(member.growth.monthly_total)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="no-data">No data</span>
                    )}
                  </div>
                </div>
                <div className="member-cell">
                  <div className="member-metric">
                    <span className="metric-label">Community:</span>
                    {member.has_inventory ? (
                      <span className="metric-value">
                        {member.latest_snapshot.community_size.toLocaleString()}
                        {member.growth?.community_size && (
                          <span 
                            className="growth-indicator"
                            style={{ color: getGrowthColor(member.growth.community_size) }}
                          >
                            {formatGrowth(member.growth.community_size)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="no-data">No data</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && (
          <div className="load-more-container">
            <button 
              className="btn-secondary load-more-btn"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>

      {/* Info Tooltip */}
      {showTooltip && tooltipData && (
        <div 
          className="info-tooltip"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y
          }}
        >
          {tooltipData}
        </div>
      )}
    </div>
  );
}

export default TeamInventorySection;
